import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import { supabase } from "../../../lib/supabase";
import { useApp } from "../../../contexts/AppContext";
import TutorialModal from "../../../components/feature/TutorialModal";

const PURPOSE_OPTIONS = [
  { value: "hobby", label: "趣味として学びたい", icon: "ri-heart-3-line", desc: "楽しみながら学べる入りやすい講座" },
  { value: "skill", label: "手に職をつけたい", icon: "ri-tools-line", desc: "実践的な技術を身につける講座" },
  { value: "career", label: "将来の仕事につなげたい", icon: "ri-briefcase-line", desc: "キャリアに活かせる継続的な学習" },
  { value: "return", label: "復職の準備をしたい", icon: "ri-refresh-line", desc: "スキルアップで自信をつける" },
  { value: "change", label: "転職・キャリアチェンジを考えている", icon: "ri-exchange-line", desc: "新しいキャリアへの第一歩" },
  { value: "business", label: "独立・副業の参考にしたい", icon: "ri-store-line", desc: "事業のヒントを得られる講座" },
  { value: "succession", label: "家業・事業承継に関心がある", icon: "ri-building-4-line", desc: "伝統・事業の継承を支える" },
  { value: "research", label: "まずは情報収集したい", icon: "ri-search-eye-line", desc: "幅広く気軽に探索" },
  { value: "other", label: "その他", icon: "ri-more-line", desc: "自由な目的で学ぶ" },
];

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    username: "",
    name: "",
    email: "",
    password: "",
    purpose: "",
    agree: false,
  });
  const { setMode, triggerTutorial } = useApp();
  const navigate = useNavigate();

  useEffect(() => { setTimeout(() => setVisible(true), 80); }, []);

  const update = (k: string, v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.agree) return;
    setError("");
    setLoading(true);

    const { error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          username: form.username,
          name: form.name,
          role: "consumer",
          plan: "free",
          purpose: form.purpose || null,
        },
      },
    });

    if (signUpError) {
      if (signUpError.message.includes("already registered")) {
        setError("このメールアドレスはすでに登録されています。");
      } else {
        setError(signUpError.message);
      }
      setLoading(false);
      return;
    }

    setMode("consumer");
    localStorage.removeItem("tsugite_tutorial_done");
    triggerTutorial();
    setLoading(false);
    navigate("/mypage");
  };

  const usernameValid = /^[a-zA-Z0-9_\-\u3040-\u309f\u30a0-\u30ff\u4e00-\u9fff]{3,20}$/.test(form.username);
  const step1Valid = form.username.trim() && usernameValid && form.name.trim() && form.email.trim() && form.password.length >= 8;
  const step2Valid = form.agree;

  return (
    <div className={`min-h-screen bg-ivory flex flex-col transition-opacity duration-700 ${visible ? "opacity-100" : "opacity-0"}`}>
      <Navbar />
      <TutorialModal />
      <main className="flex-1 flex items-center justify-center px-4 py-20 pt-28">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <p className="font-display italic text-gold text-xs tracking-widest mb-2">Join TSUGITE</p>
            <h1 className="font-serif text-deep-green text-3xl font-light">新規登録</h1>
            <p className="text-xs text-charcoal/50 font-sans mt-2">
              登録後、受講者モード・事業者モードをいつでも切り替えられます
            </p>
          </div>

          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-sans font-medium transition-all duration-300 ${step >= s ? "bg-deep-green text-white" : "bg-stone-200 text-charcoal/40"}`}>
                  {step > s ? <i className="ri-check-line" /> : s}
                </div>
                {s < 2 && <div className={`w-12 h-px transition-all duration-300 ${step >= s + 1 ? "bg-deep-green" : "bg-stone-200"}`} />}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-8 border border-stone-100">
            {step === 1 && (
              <div className="flex flex-col gap-5">
                <p className="font-serif text-deep-green text-lg">基本情報</p>
                <div>
                  <label className="text-xs text-charcoal/50 font-sans mb-1.5 block">ユーザーネーム <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <i className="ri-at-line absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/30 text-sm" />
                    <input
                      value={form.username}
                      onChange={(e) => update("username", e.target.value)}
                      placeholder="tsugite_user（3〜20文字）"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 text-sm font-sans text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-teal transition-colors bg-ivory"
                    />
                  </div>
                  {form.username.length > 0 && !usernameValid && (
                    <p className="text-xs text-red-400 font-sans mt-1">3〜20文字で入力してください</p>
                  )}
                  {form.username.length > 0 && usernameValid && (
                    <p className="text-xs text-teal font-sans mt-1 flex items-center gap-1"><i className="ri-check-line" />@{form.username} として表示されます</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-charcoal/50 font-sans mb-1.5 block">お名前 <span className="text-red-400">*</span></label>
                  <input
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="山田 太郎"
                    className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm font-sans text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-teal transition-colors bg-ivory"
                  />
                  <p className="text-xs text-charcoal/40 font-sans mt-1">本名は管理目的のみに使用され、公開されません。</p>
                </div>
                <div>
                  <label className="text-xs text-charcoal/50 font-sans mb-1.5 block">メールアドレス <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/30 text-sm" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="example@email.com"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 text-sm font-sans text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-teal transition-colors bg-ivory"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-charcoal/50 font-sans mb-1.5 block">パスワード <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/30 text-sm" />
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => update("password", e.target.value)}
                      placeholder="8文字以上"
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-stone-200 text-sm font-sans text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-teal transition-colors bg-ivory"
                    />
                  </div>
                  {form.password.length > 0 && form.password.length < 8 && (
                    <p className="text-xs text-red-400 font-sans mt-1">8文字以上で入力してください</p>
                  )}
                </div>
                <button
                  onClick={() => setStep(2)}
                  disabled={!step1Valid}
                  className="w-full py-4 rounded-full text-sm font-sans font-medium cursor-pointer transition-all duration-300 btn-primary disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  次へ →
                </button>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleRegister} className="flex flex-col gap-5">
                <div>
                  <p className="font-serif text-deep-green text-lg mb-0.5">どのような目的で学びたいですか？</p>
                  <p className="text-xs text-charcoal/50 font-sans">あなたに合った講座や事業者を見つけやすくするため、最も近いものを1つ選んでください。</p>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {PURPOSE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => update("purpose", opt.value)}
                      className={`flex items-center gap-4 w-full text-left px-4 py-3.5 rounded-xl border-2 cursor-pointer transition-all duration-200 group ${form.purpose === opt.value ? "border-deep-green bg-deep-green/4" : "border-stone-200 hover:border-teal/50 hover:bg-stone-50"}`}
                    >
                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors ${form.purpose === opt.value ? "bg-deep-green/10" : "bg-stone-100 group-hover:bg-teal/10"}`}>
                        <i className={`${opt.icon} text-base ${form.purpose === opt.value ? "text-deep-green" : "text-charcoal/50 group-hover:text-teal"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-sans font-medium transition-colors ${form.purpose === opt.value ? "text-deep-green" : "text-charcoal"}`}>{opt.label}</p>
                        <p className="text-xs text-charcoal/40 font-sans mt-0.5">{opt.desc}</p>
                      </div>
                      {form.purpose === opt.value && (
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-deep-green flex-shrink-0">
                          <i className="ri-check-line text-white text-xs" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-charcoal/40 font-sans text-center">
                  <i className="ri-information-line mr-1" />
                  この設定はマイページからいつでも変更できます（任意）
                </p>

                <label className="flex items-start gap-2.5 cursor-pointer mt-1">
                  <input
                    type="checkbox"
                    checked={form.agree}
                    onChange={(e) => update("agree", e.target.checked)}
                    className="mt-0.5 cursor-pointer accent-teal"
                  />
                  <span className="text-xs text-charcoal/60 font-sans leading-relaxed">
                    <Link to="/terms" className="text-teal hover:underline">利用規約</Link>・
                    <Link to="/privacy" className="text-teal hover:underline">プライバシーポリシー</Link>に同意します。<span className="text-red-400">*</span>
                  </span>
                </label>

                {error && (
                  <p className="text-xs text-red-400 font-sans bg-red-50 rounded-lg px-3 py-2 flex items-center gap-1.5">
                    <i className="ri-error-warning-line" />{error}
                  </p>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 py-3.5 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 hover:border-teal hover:text-teal transition-colors cursor-pointer whitespace-nowrap"
                  >
                    ← 戻る
                  </button>
                  <button
                    type="submit"
                    disabled={!step2Valid || loading}
                    className="flex-[2] px-8 py-3.5 rounded-full text-sm font-sans font-medium cursor-pointer transition-all duration-300 whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed btn-primary"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin" />登録中...
                      </span>
                    ) : "登録する →"}
                  </button>
                </div>
              </form>
            )}
          </div>

          <p className="text-center text-sm font-sans text-charcoal/50 mt-6">
            すでにアカウントをお持ちの方は
            <Link to="/login" className="text-teal hover:text-deep-green ml-1 cursor-pointer">ログイン →</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
